import { request } from "@shared/utils";
import { API, METHODS } from "@shared/constants";
import {
  AccountPaymentShape,
  CheckAccountShape,
  CreateAccountInterface,
  GetInvoicesShape,
  GoToBillingPortalShape,
  GoToCheckoutShape,
  LinkAccountPropertyListToPropertiesFromFileInterface,
  LinkAccountPropertyListToPropertyInterface,
  PayInvoiceShape,
  UpdateAccountInterface,
  UpdateSelfAccountInterface,
} from "@containers/Account/interface";
import { BaseFilter, WithCallback } from "@shared/interfaces";
import {
  AdjustAccountBalanceRequest,
  GetAccountEntity,
  SaveAccountAttempts,
  UpdatePrimaryUserPayload,
} from "@shared/models";

export default {
  getAccountScmManager: ({ id }: { id: number }) => request(METHODS.GET, API.ACCOUNT.GET_CSM_MANAGER(id))(),
  getCheckoutUrl: (params: GoToCheckoutShape) => request(METHODS.GET, API.ACCOUNT.GET_CHECKOUT_URL)({ params }),
  getBillingPortalUrl: (params: GoToBillingPortalShape) =>
    request(METHODS.GET, API.ACCOUNT.GET_BILLING_PORTAL_URL)({ params }),
  checkPaymentMethodExistence: () => request(METHODS.GET, API.ACCOUNT.CHECK_PAYMENT_METHOD_EXISTENCE)(),
  getInvoices: (params: GetInvoicesShape) => request(METHODS.GET, API.ACCOUNT.GET_INVOICES)({ params }),
  payInvoice: ({ invoiceId }: PayInvoiceShape) => request(METHODS.POST, API.ACCOUNT.PAY_INVOICE(invoiceId))(),
  getAccounts: (payload: BaseFilter) => request(METHODS.GET, API.ACCOUNT.GET_ACCOUNTS_LIST)({ params: payload }),
  accountPayment: (payload: AccountPaymentShape) =>
    request(METHODS.POST, API.ACCOUNT.ACCOUNT_PAYMENT)({ amount: payload.amount }),
  getAccountInvoice: (payload: { invoiceId: number }) =>
    request(METHODS.GET, API.ACCOUNT.GET_ACCOUNT_INVOICE(payload.invoiceId))(),

  checkAccount: (payload: CheckAccountShape) => request(METHODS.GET, API.ACCOUNT.CHECK_ACCOUNT)({ params: payload }),

  createAccount: (payload: CreateAccountInterface) => request(METHODS.POST, API.ACCOUNT.CREATE_ACCOUNT)(payload),
  updateAccount: ({ id, ...payload }: UpdateAccountInterface) =>
    request(METHODS.PUT, API.ACCOUNT.UPDATE_ACCOUNT(id))(payload),
  deleteAccount: (id: number) => request(METHODS.DELETE, API.ACCOUNT.DELETE_ACCOUNT(id))(),
  updateCurrentAccount: (payload: UpdateSelfAccountInterface) =>
    request(METHODS.PATCH, API.ACCOUNT.UPDATE_CURRENT)(payload),
  setActiveAccount: (id: number) => request(METHODS.POST, API.ACCOUNT.SET_ACTIVE_ACCOUNT)({ id }),
  activateAccount: (id: number) => request(METHODS.PUT, API.ACCOUNT.ACTIVATE)({ id }),
  deactivateAccount: (id: number) => request(METHODS.PUT, API.ACCOUNT.DEACTIVATE)({ id }),

  getSubscription: (id: number) => request(METHODS.GET, API.ACCOUNT.GET_SUBSCRIPTION(id))(),
  getSubscriptions: () => request(METHODS.GET, API.ACCOUNT.GET_SUBSCRIPTIONS)(),
  updateSubscription: (id: number, counties: number[]) =>
    request(METHODS.PUT, API.ACCOUNT.UPDATE_SUBSCRIPTION(id))({ counties }),

  getAccount: (payload: { id: number }) => {
    const { id } = payload;
    return request(METHODS.GET, API.ACCOUNT.GET_ACCOUNT(id))();
  },
  syncAccount: (payload: { id: number }) => {
    const { id } = payload;
    return request(METHODS.POST, API.ACCOUNT.SYNC_ACCOUNT(id))();
  },
  getAccountPropertyLists: () => request(METHODS.GET, API.ACCOUNT.GET_ACCOUNT_PROPERTY_LISTS)(),
  createAccountPropertyList: ({ name }: { name: string }) => {
    return request(METHODS.POST, API.ACCOUNT.CREATE_ACCOUNT_PROPERTY_LIST)({ name });
  },
  getAccountNotes: (params: GetAccountEntity) => {
    return request(METHODS.GET, API.ACCOUNT.GET_ACCOUNT_NOTES)({ params });
  },
  createAccountNote: (payload: GetAccountEntity & { text: string }) => {
    const { type, entity, text } = payload;
    return request(METHODS.POST, API.ACCOUNT.CREATE_ACCOUNT_NOTE)({ text, type, entity });
  },
  getAccountAttempts: (payload: WithCallback<GetAccountEntity>) => {
    const { type, entity } = payload;
    return request(METHODS.GET, API.ACCOUNT.GET_ACCOUNT_ATTEMPTS)({ params: { type, entity } });
  },
  saveAccountAttempts: (payload: SaveAccountAttempts) => {
    const { type, entity, call, mail, message } = payload;
    return request(METHODS.PUT, API.ACCOUNT.SAVE_ACCOUNT_ATTEMPTS)({ call, mail, message, type, entity });
  },
  updatePrimaryUser: (payload: UpdatePrimaryUserPayload) => {
    return request(METHODS.PUT, API.ACCOUNT.UPDATE_PRIMARY_USER(payload.account_id))({ user_id: payload.user_id });
  },
  linkAccountPropertyListToPropertiesFromFile: (payload: LinkAccountPropertyListToPropertiesFromFileInterface) => {
    const { fileId, listIds } = payload;
    return request(METHODS.POST, API.ACCOUNT.LIST_ACCOUNT_PROPERTY_LIST_TO_PROPERTIES_FROM_FILE)({ fileId, listIds });
  },
  linkAccountPropertyListToProperty: (payload: LinkAccountPropertyListToPropertyInterface) => {
    const { propertyId, listIds } = payload;
    return request(METHODS.POST, API.ACCOUNT.LIST_ACCOUNT_PROPERTY_LIST_TO_PROPERTY)({ propertyId, listIds });
  },
  getFeatures: () => request(METHODS.GET, API.ACCOUNT.GET_FEATURES)(),
  moveUserToAnotherCompany: (payload: { accountId: number; oldAccountId: number; userId: number }) =>
    request(METHODS.POST, API.ACCOUNT.MOVE_USER_TO_ANOTHER_COMPANY)(payload),

  adjustAccountBalance: ({ account_id, ...payload }: AdjustAccountBalanceRequest) => {
    return request(METHODS.POST, API.ACCOUNT.ADJUST_BALANCE(account_id))(payload);
  },
};
